import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  isMoveValid,
  parseMove,
  getPrevMoveNotation,
  getCurrentVariation,
  onNextMove as chessNextMove,
  onPrevMove as chessPrevMove,
  getLastMove,
  getMove,
} from '../../util/Chess';
import UndoConfirmModal from '../modals/UndoConfirmModal';

const MOVE_TIME = 750; // 0.75 seconds

const Controls = ({
  game,
  moves,
  setMoves,
  currentMove,
  handleCurrentMoveUpdate,
  showReset,
  setPgn,
  showUndo,
  isAnnotationFocused = false,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const playingInterval = useRef(null);
  const [showUndoModal, setShowUndoModal] = React.useState(false);
  const [isSpaceDown, setIsSpaceDown] = useState(false);

  // Add a ref to track if we're at starting position to use in global handler
  const isAtStartingPositionRef = useRef(currentMove === '-1' || !currentMove || currentMove === '');

  // CRITICAL FIX: Add a ref to maintain the latest move with variation path
  // This helps ensure we don't lose the variation path between keypresses
  const currentMoveRef = useRef(currentMove);

  // Update the refs whenever currentMove changes
  useEffect(() => {
    isAtStartingPositionRef.current = (currentMove === '-1' || !currentMove || currentMove === '');
    currentMoveRef.current = currentMove;


  }, [currentMove]);

  // ULTRA AGGRESSIVE APPROACH: Add a direct DOM-level event capture
  // This is separate from React's event system and will completely block LEFT ARROW
  useEffect(() => {
    // Define an aggressive event capture function that runs BEFORE all other handlers
    function blockLeftArrowAtStartPosition(event) {
      // Only block LEFT ARROW key
      if (event.key === 'ArrowLeft') {
        // Check if we're at starting position using the ref (for up-to-date value)
        if (isAtStartingPositionRef.current === true) {

          // Absolutely prevent default behavior
          event.preventDefault();
          // Stop propagation to any other listeners
          event.stopPropagation();
          // Stop immediate propagation to ensure no other handlers run
          event.stopImmediatePropagation();
          // Return false to try to cancel the event in older browsers
          return false;
        }
      }
    }

    // Add our handler with 'capture: true' so it runs BEFORE any other handlers
    document.addEventListener('keydown', blockLeftArrowAtStartPosition, { capture: true });

    // Clean up on unmount
    return () => {
      document.removeEventListener('keydown', blockLeftArrowAtStartPosition, { capture: true });
    };
  }, []);

  // Add a key handling cooldown to prevent rapid keyboard navigation issues
  const [isKeyHandlingCooldown, setIsKeyHandlingCooldown] = useState(false);

  // Create refs for all the control buttons to programmatically click them from hotkeys
  const resetButtonRef = useRef(null);
  const prevButtonRef = useRef(null);
  const playButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const endButtonRef = useRef(null);

  // Find the last move that belongs to the main line (not a comment)
  const getLastMainLineMove = () => {
    if (!moves || moves.length === 0) return '-1';

    // Start from index 0 and find the highest index move that is part of the main line
    let lastMainLineIdx = -1;

    for (let i = 0; i < moves.length; i++) {
      // Check if this move is part of the main line (not a comment)
      if (!moves[i].creator || moves[i].creator.type !== 'COMMENT') {
        lastMainLineIdx = i;
      } else {
        // Once we hit a commented move, we're out of the main line
        break;
      }
    }

    return lastMainLineIdx.toString();
  };

  // Helper function to check if a move would transition from main line to comment
  const wouldTransitionToComment = (current, next) => {
    if (!isMoveValid(next, moves)) {
      return false; // Not a valid next move
    }

    const currentMoveObj = getMove(moves, current);
    const nextMoveObj = getMove(moves, next);

    return (
      // Current move is main line (not a comment)
      (!currentMoveObj?.creator || currentMoveObj?.creator?.type !== 'COMMENT') &&
      // Next move is a comment
      nextMoveObj?.creator?.type === 'COMMENT'
    );
  };

  // Helper function to check if we're at end of main line
  const isAtEndOfMainLine = () => {
    if (!moves || moves.length === 0) return true;

    // If we're already in a commented line, we're not at the end of main line
    const currentMoveObj = getMove(moves, currentMove);
    if (currentMoveObj?.creator?.type === 'COMMENT') {
      return false;
    }

    // Get the last move index of the main line
    const lastMainLineIdx = getLastMainLineMove();

    // Check if current move is the last move of the main line
    return currentMove === lastMainLineIdx;
  };

  // Helper function to check if a move is in a variation
  const isInVariation = (moveNotation) => {
    // Basic validation
    if (!moveNotation || moveNotation === '-1' || moveNotation === '') {
      return false;
    }

    // Primary check: If the notation contains a pipe character, it's a variation
    if (moveNotation.includes('|')) {
      return true;
    }

    // Secondary check: Look for index-based validation in the moves array
    const moveIndex = parseInt(moveNotation, 10);

    // Validate the index
    if (isNaN(moveIndex) || moveIndex < 0 || moveIndex >= moves.length) {
      return false;
    }

    // A move is in a variation if it has a creator of type COMMENT
    // This is for backward compatibility with older data format
    const move = moves[moveIndex];
    return move?.creator?.type === 'COMMENT';
  };

  // Helper function to get the variation path of a move
  const getVariationPath = (moveNotation) => {
    if (!moveNotation || moveNotation === '-1' || moveNotation === '') {
      return null;
    }

    if (!moveNotation.includes('|')) {
      return null; // Not in a variation
    }

    return moveNotation.split('|')[0];
  };

  // UPDATED: Check for variations using the correct data structure
  const hasVariationBranches = (moveIdx) => {
    // First check if we're in the main line
    if (isInVariation(moveIdx)) {
      return false; // This move is already in a variation, not what we're looking for
    }

    // Simple validation
    if (!moveIdx || moveIdx === '-1' || moveIdx === '') {
      return false; // Starting position or invalid move
    }

    // Convert moveIdx to a number for array access
    const moveIndex = parseInt(moveIdx, 10);

    // Validate the index
    if (isNaN(moveIndex) || moveIndex < 0 || moveIndex >= moves.length) {
      return false;
    }

    // Get the actual move object
    const move = moves[moveIndex];

    // CRITICAL: Directly check if the move has a 'variations' property
    // This matches the actual database structure from your example
    if (move && move.variations && Array.isArray(move.variations) && move.variations.length > 0) {

      return true;
    }

    // Legacy checks - keeping for backward compatibility

    // Check if any moves have this move as parent in creator/parentMoveId structure
    for (let i = 0; i < moves.length; i++) {
      const otherMove = moves[i];

      // Check if this move has creator info and points to our current move as parent
      if (otherMove?.creator?.type === 'COMMENT' && otherMove?.parentMoveId === moveIndex) {
        return true;
      }
    }

    // Check for variations using pipe notation format
    for (let i = 0; i < moves.length; i++) {
      const moveNotation = i.toString();

      // Skip non-variation moves
      if (!isInVariation(moveNotation)) continue;

      const parts = moveNotation.split('|');
      if (parts.length > 0) {
        const parent = parts[0].split(':')[0]; // Extract parent index
        if (parent === moveIdx) {

          return true;
        }
      }
    }

    return false;
  };

  // UPDATED: Helper function to find the first move of a variation
  const getFirstMoveInVariation = (parentMoveIdx) => {
    if (!parentMoveIdx || parentMoveIdx === '-1' || parentMoveIdx === '') {
      return null; // Invalid parent move
    }


    // Convert parent move index to a number
    const parentIndex = parseInt(parentMoveIdx, 10);

    // Validate the index
    if (isNaN(parentIndex) || parentIndex < 0 || parentIndex >= moves.length) {
      return null;
    }

    // Get the actual move object
    const parentMove = moves[parentIndex];

    // CRITICAL: First check if the move has a 'variations' array property
    // This matches the database structure from your example
    if (parentMove && parentMove.variations && Array.isArray(parentMove.variations) && parentMove.variations.length > 0) {
      // We found variations! Get the first variation
      const firstVariation = parentMove.variations[0];

      if (Array.isArray(firstVariation) && firstVariation.length > 0) {
        const firstMove = firstVariation[0];

        // Create a unique variation identifier for this first move
        // We'll use a special format: "original_index|variation_number:move_in_variation"
        // For example: "5|0:0" means "the first move of the first variation of move at index 5"
        const variationId = `${parentIndex}|0:0`;

        return variationId;
      }
    }

    // Fallback to legacy approaches if we don't have a variations array

    // First try with move objects directly - look for the earliest comment move with this parent
    let firstVariationMove = null;
    let earliestVariationIndex = Number.MAX_SAFE_INTEGER;

    for (let i = 0; i < moves.length; i++) {
      const move = moves[i];

      // Check if this is a comment move with the right parent
      if (move?.creator?.type === 'COMMENT' && move?.parentMoveId === parentIndex) {
        // We want the earliest variation move (lowest index)
        if (i < earliestVariationIndex) {
          earliestVariationIndex = i;
          firstVariationMove = i.toString();

        }
      }
    }

    // If we found a variation move, return it
    if (firstVariationMove) {
      return firstVariationMove;
    }

    // Fallback to the notation-based approach
    let lowestMoveIndex = Number.MAX_SAFE_INTEGER;
    let bestVariationMove = null;

    for (let i = 0; i < moves.length; i++) {
      const moveNotation = i.toString();

      // Skip non-variation moves
      if (!isInVariation(moveNotation)) continue;

      // Check if this move has our parent
      const parts = moveNotation.split('|');
      if (parts.length < 1) continue;

      const parent = parts[0].split(':')[0]; // Extract parent index

      if (parent === parentMoveIdx) {
        // Determine the move index within the variation
        let moveIndex = 0;

        if (parts[1]) {
          if (parts[1].includes(':')) {
            // For format like "1|0:0", get the index after the colon
            moveIndex = parseInt(parts[1].split(':')[1], 10);
          } else {
            // For format like "1|0", use the number after the pipe
            moveIndex = parseInt(parts[1], 10);
          }
        }



        // If this move has a lower index in the variation, it's our new candidate
        if (moveIndex < lowestMoveIndex) {
          lowestMoveIndex = moveIndex;
          bestVariationMove = moveNotation;

        }
      }
    }

    return bestVariationMove;
  };

  // NEW: Helper function to get the parent move of a variation
  const getParentMove = (variationMoveIdx) => {
    if (!isInVariation(variationMoveIdx)) {
      return null; // Not in a variation
    }

    // Split the move notation to extract the parent
    const parts = variationMoveIdx.split('|');
    if (!parts || parts.length < 1) return null;

    // Handle both standard and colon-based formats
    const parentPart = parts[0];
    const parentMove = parentPart.includes(':')
      ? parentPart.split(':')[0]
      : parentPart;

    return parentMove;
  };

  const onNextMove = (currentMove, setCurrentMoveCallback) => {
    

    // CRITICAL FIX: Always handle index=-1 consistently, regardless of history
    if (currentMove === '-1' || !currentMove || currentMove === '') {
      
      if (moves.length > 0) {
        if (typeof setCurrentMoveCallback === 'function') {
          setCurrentMoveCallback('0');
        } else {
          handleCurrentMoveUpdate('0');
        }
      }
      return;
    }

    const { prefix, currMoveNum } = parseMove(currentMove);
    const nextMoveNotation = `${prefix}${currMoveNum + 1}`;

    const isValid = isMoveValid(nextMoveNotation, moves);
    

    if (isValid) {
      if (wouldTransitionToComment(currentMove, nextMoveNotation)) {
        return; // Prevent the navigation
      }

      // Proceed with the move navigation

      // Use the callback if provided (for hotkeys), otherwise use state setter directly
      if (typeof setCurrentMoveCallback === 'function') {
        setCurrentMoveCallback(nextMoveNotation);
      } else {
        handleCurrentMoveUpdate(nextMoveNotation);
      }
    } else {
      
      // Callback is NOT called here, so currentMove should not change due to this onNextMove path
      return;

    }
  };

  const onPrevMove = (currentMove, setCurrentMoveCallback) => {
    // Enhanced logging for debugging

    ;

    // IMPORTANT: If already at starting position, do nothing
    if (currentMove === '-1' || !currentMove || currentMove === '') {
      return;
    }

    // Handle special case when current move is "0" (first move)
    if (currentMove === '0') {
      // Go to starting position using callback or direct state update
      if (typeof setCurrentMoveCallback === 'function') {
        setCurrentMoveCallback('-1');
      } else {
        handleCurrentMoveUpdate('-1');
      }
      return;
    }

    // Parse the current move to handle variation paths
    const { prefix, currMoveNum } = parseMove(currentMove);

    // If we're at the first move of a variation (index 0), handle special case
    if (currMoveNum === 0 && prefix && prefix.includes('|')) {
      // We need to go back to the parent move in the main line
      const parentMove = prefix.split('|')[0];

      // Use callback or direct state update
      if (typeof setCurrentMoveCallback === 'function') {
        setCurrentMoveCallback(parentMove);
      } else {
        handleCurrentMoveUpdate(parentMove);
      }
      return;
    }

    // Use getPrevMoveNotation instead of numeric subtraction
    const prevMove = getPrevMoveNotation(currentMove);
    if (prevMove) {


      // Use callback or direct state update
      if (typeof setCurrentMoveCallback === 'function') {
        setCurrentMoveCallback(prevMove);
      } else {
        handleCurrentMoveUpdate(prevMove);
      }
    }
  };

  const onUndoMove = () => {
    const { prefix, currMoveNum } = parseMove(currentMove);
    let nextMoveIdx = `${prefix}${currMoveNum + 1}`;
    if (isMoveValid(nextMoveIdx, moves)) {
      setShowUndoModal(true);
      return;
    }

    const prevMove = getPrevMoveNotation(currentMove);
    let updatedPostMoves = JSON.parse(JSON.stringify(moves));
    if (!updatedPostMoves) {
      return;
    }
    const variation = getCurrentVariation(updatedPostMoves, currentMove);
    variation.pop();
    setMoves(updatedPostMoves);
    handleCurrentMoveUpdate(prevMove, false);
    game.undo();
    setPgn(game.pgn());
  };

  const handleConfirmUndo = () => {
    const { prefix, currMoveNum } = parseMove(currentMove);
    let updatedPostMoves = JSON.parse(JSON.stringify(moves));
    const variation = getCurrentVariation(updatedPostMoves, currentMove);

    // Remove all moves after current position
    variation.splice(currMoveNum);

    setMoves(updatedPostMoves);

    // Reset game to previous position
    const prevMove = getPrevMoveNotation(currentMove);
    handleCurrentMoveUpdate(prevMove, false);

    // Reset the game's FEN to the previous position
    const moveHistory = game.history();
    game.reset();
    for (let i = 0; i < currMoveNum; i++) {
      game.move(moveHistory[i]);
    }
    setPgn(game.pgn());
    setShowUndoModal(false);
  };

  const handleJumpToEnd = () => {
    const { prefix } = parseMove(currentMove);
    // Find the last move in the current variation
    const variation = getCurrentVariation(moves, currentMove);
    const lastMoveInVariation = `${prefix}${variation.length - 1}`;
    handleCurrentMoveUpdate(lastMoveInVariation);
    setShowUndoModal(false);
  };

  const onReset = () => {
    handleCurrentMoveUpdate('-1');
  };

  const onEnd = (currentMoveParam, callback) => {
    // Use the provided parameter if available, otherwise use the component state
    const moveToUse = currentMoveParam || currentMove;

    // If we're on the main line (not in a variation)
    if (!isInVariation(moveToUse)) {
      // Go to the last main line move only
      const lastMainLineIdx = getLastMainLineMove();
      if (moveToUse === lastMainLineIdx) {
        // Already at the end of the main line, do nothing
        return;
      }
      // Support the callback pattern for keyboard navigation
      if (typeof callback === 'function') {
        callback(lastMainLineIdx);
      } else {
        handleCurrentMoveUpdate(lastMainLineIdx);
      }
      return;
    }

    // If in a variation, go to the end of the current variation as before
    const { prefix } = parseMove(moveToUse);

    // Get the current variation based on our current position
    const variation = getCurrentVariation(moves, moveToUse);

    if (!variation || variation.length === 0) {

      return;
    }

    // Go to the last move in the CURRENT variation (maintain variation path)
    const lastMoveInVariation = `${prefix}${variation.length - 1}`;


    // Support the callback pattern for keyboard navigation
    if (typeof callback === 'function') {
      callback(lastMoveInVariation);
    } else {
      handleCurrentMoveUpdate(lastMoveInVariation);
    }
  };

  // Handle space bar toggle play/pause
  const handleSpacebarPress = () => {
    // Programmatically click the play/pause button
    if (playButtonRef.current) {
      playButtonRef.current.click();
    }
  };

  // Make a simpler keyDownHandler with more consistent behavior 
  const keyDownHandler = useCallback(
    (e) => {
      // Skip ALL handling if annotation textarea is focused
      if (isAnnotationFocused) {
        if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
          // Skip key handling when annotation is focused
        }
        return; // Exit immediately, allowing normal text editing behavior
      }

      // Skip handling if in cooldown period (prevents race conditions from rapid keypresses)
      if (isKeyHandlingCooldown) {
        return;
      }

      // Prevent default behavior for arrow keys to avoid page scrolling
      if (['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
        e.preventDefault();
      }

      // CRITICAL: For keyboard navigation, ALWAYS use the currentMoveRef
      // This ensures we're using the most up-to-date value that includes variation paths
      const stableCurrentMove = String(currentMoveRef.current || '');

      // For space key, only handle if it wasn't already pressed
      if (e.key === ' ' && !isSpaceDown) {
        setIsSpaceDown(true);
        handleSpacebarPress();
      } else if (e.key === 'ArrowRight') {
        // Prevent default browser behavior
        e.preventDefault();

        // VARIATION IMPROVEMENT: Check if next move would be in a variation
        const { prefix, currMoveNum } = parseMove(stableCurrentMove);
        const nextMoveIdx = `${prefix}${currMoveNum + 1}`;
        
        // Check if this would be a variation move
        const isVariationMove = nextMoveIdx.includes('|') || stableCurrentMove.includes('|');
        
        // If this is a variation move, set the direct variation flag and preload
        if (isVariationMove && isMoveValid(nextMoveIdx, moves)) {
          try {
            // Set global flag for direct variation transition
            window.chessDirectVariationMove = {
              moveNotation: nextMoveIdx,
              timestamp: Date.now()
            };
            
            // Preload the position if possible
            if (window.preloadVariationPosition) {
              window.preloadVariationPosition(nextMoveIdx);
            }
          } catch (e) {
            console.error('Error setting variation flags:', e);
          }
        }

        // Call onNextMove as usual
        onNextMove(stableCurrentMove, (newMove) => {
          currentMoveRef.current = newMove;
          handleCurrentMoveUpdate(newMove);
        });

      } else if (e.key === 'ArrowLeft') {
        // Simple check - this is just a backup, our global handler should have already blocked it at starting position
        if (isAtStartingPositionRef.current) {
          return;
        }
        
        // Calculate previous move and preload if it would be a variation
        try {
          const prevMove = getPrevMoveNotation(stableCurrentMove);
          
          // Check if this would be a variation move
          const isVariationMove = prevMove.includes('|');
          
          // If this is a variation move, set the direct variation flag and preload
          if (isVariationMove) {
            // Set global flag for direct variation transition
            window.chessDirectVariationMove = {
              moveNotation: prevMove,
              timestamp: Date.now()
            };
            
            // Preload the position if possible
            if (window.preloadVariationPosition) {
              window.preloadVariationPosition(prevMove);
            }
          }
        } catch (e) {
          console.error('Error preloading previous move:', e);
        }

        // We ALWAYS use the stable copy from the ref to ensure variation path is preserved
        onPrevMove(stableCurrentMove, (newMove) => {
          // CRITICAL FIX: Immediately update the ref to ensure next keypress gets the correct value
          // This ensures we don't lose the variation path if user presses key faster than React updates
          currentMoveRef.current = newMove;
          handleCurrentMoveUpdate(newMove);
        });
      } else if (e.key === 'ArrowUp') {
        // NEW BEHAVIOR: If we're in a variation, go to parent move in main line
        if (isInVariation(stableCurrentMove)) {
          const parentMove = getParentMove(stableCurrentMove);
          if (parentMove) {
            // CRITICAL: Update ref first for immediate consistency
            currentMoveRef.current = parentMove;
            
            // Set direct variation flag to avoid delay
            try {
              window.chessDirectVariationMove = {
                moveNotation: parentMove, // Main line move is not actually a variation
                timestamp: Date.now()
              };
            } catch (e) {
              console.error('Error setting variation flags:', e);
            }
            
            handleCurrentMoveUpdate(parentMove);
          } else {
            // Fallback to original reset behavior if parent not found
            currentMoveRef.current = '-1';
            onReset();
          }
        } else {
          // Default behavior - go to starting position
          currentMoveRef.current = '-1';
          onReset();
        }
      } else if (e.key === 'ArrowDown') {
        // --- REFINED: Down arrow brings you from main line to first variation if it exists, otherwise to last main line move only (never into comments) ---
        const stableCurrentMove = String(currentMoveRef.current || '');
        if (!isInVariation(stableCurrentMove)) {
          // If on main line and there is a variation branch, jump to first move of first variation
          if (hasVariationBranches(stableCurrentMove)) {
            const firstVariationMove = getFirstMoveInVariation(stableCurrentMove);
            if (firstVariationMove) {
              // Set direct variation flag to avoid delay
              try {
                window.chessDirectVariationMove = {
                  moveNotation: firstVariationMove,
                  timestamp: Date.now()
                };
                
                // Preload the position if possible
                if (window.preloadVariationPosition) {
                  window.preloadVariationPosition(firstVariationMove);
                }
              } catch (e) {
                console.error('Error setting variation flags:', e);
              }
              
              currentMoveRef.current = firstVariationMove;
              handleCurrentMoveUpdate(firstVariationMove);
              return;
            }
          }
          // Otherwise, go to last main line move only (never into comments)
          const lastMainLineIdx = getLastMainLineMove();
          if (stableCurrentMove !== lastMainLineIdx) {
            currentMoveRef.current = lastMainLineIdx;
            handleCurrentMoveUpdate(lastMainLineIdx);
          }
          // If already at last main line move, do nothing
          return;
        }
        // If in a variation, allow original behavior (which is to go to end of current variation)
        onEnd(stableCurrentMove, (newMove) => {
          // Set direct variation flag to avoid delay
          try {
            window.chessDirectVariationMove = {
              moveNotation: newMove,
              timestamp: Date.now()
            };
          } catch (e) {
            console.error('Error setting variation flags:', e);
          }
          
          currentMoveRef.current = newMove;
          handleCurrentMoveUpdate(newMove);
        });
      }
    },
    // CRITICAL FIX: Remove currentMove from dependency array to prevent stale closures
    // Instead we'll ALWAYS use currentMoveRef.current which is updated in real-time
    [isSpaceDown, moves, isAnnotationFocused, isKeyHandlingCooldown],
  );

  const keyUpHandler = useCallback((e) => {
    // Skip handling if annotation textarea is focused
    if (isAnnotationFocused) {
      // Add logging for space key being ignored on key up
      if (e.key === ' ') {

      }
      return;
    }

    if (e.key === ' ') {
      setIsSpaceDown(false);
    }
  }, [isAnnotationFocused]);

  useEffect(() => {
    // Only add keyboard event listeners if the annotation is NOT focused
    if (isAnnotationFocused) {
      document.removeEventListener('keydown', keyDownHandler);
      document.removeEventListener('keyup', keyUpHandler);
      return;
    }

    document.removeEventListener('keydown', keyDownHandler);
    document.removeEventListener('keyup', keyUpHandler);
    document.addEventListener('keydown', keyDownHandler);
    document.addEventListener('keyup', keyUpHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
      document.removeEventListener('keyup', keyUpHandler);
    };
  }, [keyDownHandler, keyUpHandler, isAnnotationFocused]);


  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    // CRITICAL FIX: Special handling for starting position in auto-play
    if (currentMove === '-1' || !currentMove || currentMove === '') {
      if (moves.length > 0) {
        // Always go to the first move (index 0) from the reset position
        currentMoveRef.current = '0'; // Update ref first
        handleCurrentMoveUpdate('0');
      } else {
        // No moves available, stop playing
        setIsPlaying(false);
      }
      return;
    }

    // Ensure we use a stable string copy of the current move from the ref
    // This guarantees we have the most up-to-date value with proper variation paths
    const stableCurrentMove = String(currentMoveRef.current);
    const { prefix, currMoveNum } = parseMove(stableCurrentMove);
    let nextMoveIdx = `${prefix}${currMoveNum + 1}`;



    // If next move is not valid, stop playing
    if (!isMoveValid(nextMoveIdx, moves)) {

      setIsPlaying(false);
      return;
    }

    // Check if we'd be navigating from main line to comment
    if (wouldTransitionToComment(stableCurrentMove, nextMoveIdx)) {

      setIsPlaying(false);
      return;
    }

    playingInterval.current = setTimeout(() => {
      // Get another stable copy at timer execution time - using the ref for reliability
      const timerStableCurrentMove = String(currentMoveRef.current);


      // Recompute the next move at timer execution time
      const { prefix: timerPrefix, currMoveNum: timerMoveNum } = parseMove(timerStableCurrentMove);
      const nextMoveNotation = `${timerPrefix}${timerMoveNum + 1}`;

      // If we would transition from main line to comment, don't trigger the click
      if (wouldTransitionToComment(timerStableCurrentMove, nextMoveNotation)) {

        setIsPlaying(false);
        return;
      }

      // Otherwise proceed with the programmatic click using our callback pattern
      if (nextButtonRef.current) {

        nextButtonRef.current.click();
      } else {
        // Fallback using our new callback approach to ensure ref is updated

        onNextMove(timerStableCurrentMove, (newMove) => {
          // Update ref immediately
          currentMoveRef.current = newMove;
          // Then update state
          handleCurrentMoveUpdate(newMove);
        });
      }
    }, MOVE_TIME);

    return () => {
      clearTimeout(playingInterval.current);
    };
  }, [isPlaying, currentMove, moves]);

  const togglePlaying = () => {


    // Check if we're at the end of the game or would transition to a comment
    const { prefix, currMoveNum } = parseMove(currentMove || '-1');
    const nextMoveIdx = `${prefix}${currMoveNum + 1}`;

    const isAtEnd = !isMoveValid(nextMoveIdx, moves);
    const wouldTransitionToCommentLine = wouldTransitionToComment(currentMove || '-1', nextMoveIdx);

    if ((isAtEnd || wouldTransitionToCommentLine) && !isPlaying) {
      // If at end or would transition to comment, reset and start playing

      onReset();
      setIsPlaying(true);
      return;
    }

    // CRITICAL FIX: Special handling for any undefined or starting position
    if (currentMove === '-1' || !currentMove || currentMove === '') {

      if (moves.length > 0) {
        // First play will go to move 0
        setIsPlaying(true);
      }
      return;
    }


    setIsPlaying(p => !p);

    if (isPlaying) {
      // Stop playing

      clearTimeout(playingInterval.current);
      return;
    }
  };

  // Add a special effect for logging currentMove changes to help with debugging
  useEffect(() => {
    const { prefix, currMoveNum } = parseMove(currentMove);
    ;

    // CRITICAL: Reset the cooldown whenever currentMove changes
    // This ensures we don't drop keypresses during state updates
    setIsKeyHandlingCooldown(false);
  }, [currentMove]);

  // NEW: Helper function to get a move from variations by a variation identifier
  const getMoveFromVariation = (variationId) => {
    if (!variationId || !variationId.includes('|')) {
      return null;
    }

    try {
      const parts = variationId.split('|');
      if (parts.length !== 2) return null;

      // Get the parent move index
      const parentMoveIdx = parseInt(parts[0], 10);
      if (isNaN(parentMoveIdx) || parentMoveIdx < 0 || parentMoveIdx >= moves.length) {

        return null;
      }

      // Get the parent move
      const parentMove = moves[parentMoveIdx];
      if (!parentMove?.variations || !Array.isArray(parentMove.variations)) {

        return null;
      }

      // Parse the variation index and move within variation
      let variationIdx = 0;
      let moveInVariationIdx = 0;

      if (parts[1].includes(':')) {
        const subParts = parts[1].split(':');
        variationIdx = parseInt(subParts[0], 10);
        moveInVariationIdx = parseInt(subParts[1], 10);
      } else {
        variationIdx = 0;
        moveInVariationIdx = parseInt(parts[1], 10);
      }

      // Validate the variation index
      if (isNaN(variationIdx) || variationIdx < 0 || variationIdx >= parentMove.variations.length) {

        return null;
      }

      // Get the variation array
      const variation = parentMove.variations[variationIdx];
      if (!Array.isArray(variation)) {

        return null;
      }

      // Validate the move index within the variation
      if (isNaN(moveInVariationIdx) || moveInVariationIdx < 0 || moveInVariationIdx >= variation.length) {

        return null;
      }

      // Return the actual move from the variation
      const move = variation[moveInVariationIdx];

      return move;
    } catch (error) {
      console.error('[DEBUG] Error parsing variation ID:', variationId, error);
      return null;
    }
  };

  return (
    <>
      {showUndoModal && (
        <UndoConfirmModal
          onClose={() => setShowUndoModal(false)}
          onConfirmUndo={handleConfirmUndo}
          onJumpToEnd={handleJumpToEnd}
        />
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginTop: '0px',
            gap: '10px',
            justifyContent: 'space-between',
            width: '540px',
          }}
        >
          {showUndo && (
            <button className="gold-hover-border play-buttons reg-buttons" style={{ border: '3px solid #ff0050', height: '60px', flex: '1', color: '#ff0050', fontSize: '20px', background: 'transparent' }} onClick={onUndoMove}>

              <i class="fa-solid fa-scissors"></i>

            </button>
          )}
          <button
            ref={resetButtonRef}
            className="gold-hover-border play-buttons reg-buttons"
            style={{ border: '3px solid gray', height: '60px', flex: '1', color: 'gray', fontSize: '20px', background: 'transparent' }}
            onClick={() => {
              // First update the ref for immediate consistency
              currentMoveRef.current = '-1';

              // Then call onReset to update state
              onReset();
            }}
          >
            <i className="fa-solid fa-backward-fast"></i>
          </button>

          <button
            ref={prevButtonRef}
            className="gold-hover-border play-buttons reg-buttons"
            style={{
              border: '3px solid gray',
              height: '60px',
              flex: '1',
              color: 'gray',
              fontSize: '20px',
              background: 'transparent',
              opacity: '1',
              cursor: 'pointer'
            }}
            onClick={() => {
              // Only allow prev move when NOT at starting position
              if (currentMoveRef.current === '-1') {

              } else {
                // Make a stable copy for consistent handling
                const stableCurrentMove = String(currentMoveRef.current);


                // Using button, so provide callback to update ref
                onPrevMove(stableCurrentMove, (newMove) => {
                  // Update ref first
                  currentMoveRef.current = newMove;
                  // Then update state
                  handleCurrentMoveUpdate(newMove);
                });
              }
            }}
          >
            <i className="fa-solid fa-chevron-right fa-rotate-180"></i>
          </button>

          <button
            ref={playButtonRef}
            className="red-hover-border play-buttons reg-buttons pause-buttons"
            style={{ border: isPlaying ? '3px solid #ff0050' : '3px solid gray', height: '60px', flex: '1', color: isPlaying ? '#ff0050' : 'gray', fontSize: '20px', background: 'transparent' }}
            onClick={togglePlaying}
          >

            {!isPlaying ? (
              <i className="fa-solid fa-play"></i>
            ) : (
              <i className="fa-solid fa-pause"></i>
            )}
          </button>

          <button
            ref={nextButtonRef}
            style={{ border: '3px solid gray', height: '60px', flex: '1', color: 'gray', fontSize: '20px', background: 'transparent' }}
            className="gold-hover-border play-buttons reg-buttons"
            onClick={() => {
              // Make a stable copy for consistent handling
              const stableCurrentMove = String(currentMoveRef.current);


              // Using button, so provide callback to update ref
              onNextMove(stableCurrentMove, (newMove) => {
                // Update ref first
                currentMoveRef.current = newMove;
                // Then update state
                handleCurrentMoveUpdate(newMove);
              });
            }}
          >
            <i className="fa-solid fa-chevron-right"></i>
          </button>
          <button
            ref={endButtonRef}
            className="gold-hover-border play-buttons reg-buttons"
            style={{ border: '3px solid gray', height: '60px', flex: '1', color: 'gray', fontSize: '20px', background: 'transparent' }}
            onClick={() => {
              // Make a stable copy for consistent handling
              const stableCurrentMove = String(currentMoveRef.current);


              // Using button, so provide callback to update ref
              onEnd(stableCurrentMove, (newMove) => {
                // Update ref first
                currentMoveRef.current = newMove;
                // Then update state
                handleCurrentMoveUpdate(newMove);
              });
            }}
          >
            <i className="fa-solid fa-forward-fast"></i>
          </button>
        </div>
      </div>
    </>
  );
};

export default Controls;
